import { useContext, useEffect, useMemo, useState } from 'react';
import T, { t } from './../../common/components/T';
import css from './Bonuses.module.scss';
import UserContext, { UserProfileContext } from '../../common/utils/getContext';
import { Tooltip, svgSprite } from '../../hybrid/wrappers';
import classNames from 'classnames';
import { getData, navigateV2 } from '../../common/utils/router';
import Skeleton from '../../widgets/plug/Skeleton/Skeleton';
import { colors, createWidgetData, skeletonElements } from './helpers';
import HighChart from '../../hybrid/library/HighCharts';
import { LangContext } from '../../common/decorators/Language';
import Plug from '../../widgets/plug/Plug/Plug';
import no_period from '../../public/src/img/plugs/no_dynamic_rewards.svg';
import CalendarFilterButton from '../../views/office/components/CalendarFilterButton/CalendarFilterButton';
import { getMemoizedData } from '../../common/utils/memo';

const RenderData = ({ chartData, setData, customCss, title, additionalButtons, filterTrigger, profileData }) => {
    const { translation } = useContext(LangContext);
    const { currencies } = useContext(UserContext);
    const currency = useMemo(
        () => (currencies?.data && currencies?.data?.find((curr) => curr?.id === chartData?.period?.currency_id)) || {},
        [chartData?.period?.currency_id, currencies]
    );
    // фільтр при збросі даних в фільтрі календаря
    const resetFilterParams = useMemo(() => {
        if (!profileData) return '';
        const { marketing_profile: m } = profileData;
        return `filter[marketing_period_id_from]=${m?.marketing_period_id || 1}&filter[marketing_period_id_to]=${
            m?.marketing_period_id_current
        }`;
    }, [profileData]);
    return (
        <div className={classNames(css.block, customCss?.wrapper, 'no-zoom')}>
            <div className={css.head}>
                <div className={css.titleWrap}>
                    <p className={css.title}>
                        {title || (
                            <T textName='officePage-blockTitle-Dynamic' defaultStr='Динамика вознаграждений' page='/office' />
                        )}
                    </p>
                    <p className={css.period}>
                        <span className={css.periodLabel}>
                            <T textName='marketing_period' defaultStr='Маркетинговый период' page='/office' />:
                        </span>{' '}
                        <span className={css.periodValue}>
                            {chartData?.period?.start_formatted === chartData?.period?.end_formatted
                                ? chartData?.period?.end_formatted
                                : `${chartData?.period?.start_formatted} - ${chartData?.period?.end_formatted}`}
                        </span>
                    </p>
                </div>
                <div className={classNames(css.headButtons, customCss?.headButtons)}>
                    <CalendarFilterButton
                        attributes={['marketing_period_id_from', 'marketing_period_id_to', 'filter']}
                        url={'/api/v1/user/marketing/bonus-history-graph?filter[filter]=1'}
                        resetFilterParams={resetFilterParams}
                        setData={setData}
                        initValue={{
                            marketing_period_id_from: { value: chartData?.period?.start, label: chartData?.period?.start },
                            marketing_period_id_to: { value: chartData?.period?.end, label: chartData?.period?.end }
                        }}
                        customClasses={{}}
                        filterTrigger={filterTrigger}
                        additionalButtons={
                            additionalButtons || [
                                {
                                    component: (
                                        <Tooltip text={<T textName='more-details' defaultStr='Подробнее' page='/office' />}>
                                            <div
                                                onClick={() => navigateV2('/reports/bonus')}
                                                className={classNames(
                                                    css.headButton,
                                                    'circleButton',
                                                    css.headButtonRotate,
                                                    customCss?.moreButton
                                                )}
                                            >
                                                {svgSprite('arrow-up', { className: css.headButtonSvg })}
                                            </div>
                                        </Tooltip>
                                    ),
                                    mobileName: <T textName='more-details' defaultStr='Подробнее' page='/office' />,
                                    mobileFunc: () => navigateV2('/reports/bonus')
                                }
                            ]
                        }
                        showMobileFilterWidth={992}
                    />
                </div>
            </div>
            <div className={css.bonuses}>
                <div className={css.bonusesAllSum}>
                    <p className={css.bonusesAllLabel}>
                        <T textName='general_bonuses_sum_for_period' defaultStr='Общая сумма бонусов за период' page='/office' />
                    </p>
                    <p>
                        <span className={css.bonusesAllValue}>{(+chartData?.allSum).toLocaleString()}</span>
                        <span className={css.bonusesAllCurrency}>{currency?.symbol}</span>
                    </p>
                </div>
                <div className={css.bonusesWrapper}>
                    {chartData?.bonuses?.map((bonus, idx) => (
                        <div className={css.bonusItem} key={bonus.id}>
                            <div style={{ backgroundColor: colors[idx] }} className={css.bonusItemCircle}></div>
                            <div className={css.bonusItemInfo}>
                                <p className={css.bonusItemTitle}>
                                    {bonus.name ? bonus.name + ` - ${t('bonus-lowercase', 'бонус', 'all', translation)}` : '-'}
                                </p>
                                <p className={css.bonusItemValue}>
                                    <span>{bonus.amount || '0'}</span>
                                    <span>{currency.symbol}</span>
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {chartData?.allSum === 0 ? (
                <Plug
                    plugTitle={t(
                        'receivedBonusesNoPeriod_stat-plugTitle',
                        'Данных за указанный период нет',
                        '/office',
                        translation
                    )}
                    plugDescription={t(
                        'receivedBonusesNoPeriod_stat-plugDescription',
                        'Измените условия фильтрации',
                        '/office',
                        translation
                    )}
                    plugImg={no_period}
                />
            ) : (
                <div className={css.graph}>
                    <HighChart componentConfig={chartData?.config} />
                </div>
            )}
        </div>
    );
};

export default function Bonuses({ customCss, title, additionalButtons, filterTrigger }) {
    const { translation } = useContext(LangContext);
    const { userToken, lang, currencies } = useContext(UserContext);
    const { profileData } = useContext(UserProfileContext);
    const [allData, setAllData] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);
    const [periods, setPeriods] = useState([]);

    useEffect(() => {
        getMemoizedData('/api/v1/periods', userToken, lang).then((res) => {
            if (Array.isArray(res?.data)) setPeriods(res?.data?.sort((a, b) => +a.id - +b.id));
        });
    }, [userToken, lang]);

    useEffect(() => {
        if (!profileData || isFirstLoaded) return;
        setIsFirstLoaded(true);
        const { marketing_profile: markData } = profileData;
        getData(
            `/api/v1/user/marketing/bonus-history-graph?filter[filter]=1&filter[marketing_period_id_from]=${
                markData?.marketing_period?.id || 1
            }&filter[marketing_period_id_to]=${markData?.marketing_period_current?.id}`,
            userToken,
            lang
        ).then(setAllData);
    }, [userToken, lang, profileData, isFirstLoaded]);

    useEffect(() => {
        if (!allData || !currencies || !periods?.length) return;
        if (allData) {
            setChartData(createWidgetData(allData, translation, currencies, periods));
        }
    }, [allData, translation, currencies, periods]);

    return (
        <Skeleton
            wait={!!chartData}
            Component={RenderData}
            componentProps={{
                chartData,
                setData: (res) => {
                    setChartData(createWidgetData(res, translation, currencies, periods));
                },
                customCss,
                title,
                additionalButtons,
                filterTrigger,
                profileData
            }}
            pattern={skeletonElements}
            wrapperStyles={css.skeletonStyles}
            lazy={true}
        />
    );
}
